import d3 from 'd3';
import './force_directed_graph.scss';
import $ from 'jquery';

class ForceDirectedGraphNode {
    _id;
    _color;

    constructor(id, color) {
        this._id = id;
        if (!color) {
            this._color = '#FFFFFF';
        } else {
            this._color = color;
        }
    }

    get id() {
        return this._id;
    }

    get color() {
        return this._color;
    }
}

const FORCE_DIRECTED_GRAPH_NODE_STROKE_CLASS = 'force-directed-graph-node-stroke';

class ForceDirectedGraph {
    w;
    h;
    svg;
    vis;
    force;
    nodes;
    links;

    /**
     * Create a new force directed graph
     * @param selectorOrElement - can take a jquery style selector or an jquery style
     * element. The constructor will append a svg element onto this base element.
     */
    constructor(selectorOrElement) {
        // set up the D3 visualisation in the specified element
        this.w = 600;
        this.h = 600;
        this.svg = d3.select(selectorOrElement).append('svg:svg');
        this.vis = this.svg
            .attr('width', '100%') // centers the graph
            .attr('height', '100%') // centers the graph
            .attr('pointer-events', 'all')
            .append('svg:g');
        this.force = d3.layout.force();
        this.nodes = this.force.nodes();
        this.links = this.force.links();
        // Make it all go
        this.update();
    }

    // Add and remove elements on the graph object
    addNode(forceDirectedGraphNode) {
        this.nodes.push({ id: forceDirectedGraphNode.id, color: forceDirectedGraphNode.color });
    }

    updateNode(forceDirectedGraphNode) {
        let n = this.findNode(forceDirectedGraphNode.id);
        if (n) {
            n.color = forceDirectedGraphNode.color;
        }
    }

    removeNode(id) {
        let links = this.links;
        let i = 0;
        let n = this.findNode(id);
        while (i < this.links.length) {
            if (links[i].source == n || links[i].target == n) {
                links.splice(i, 1);
            } else {
                i++;
            }
        }
        this.nodes.splice(this.findNodeIndex(id), 1);
    }

    removeLink(source, target) {
        let links = this.links;
        for (var i = 0; i < links.length; i++) {
            if (links[i].source.id == source && links[i].target.id == target) {
                links.splice(i, 1);
                break;
            }
        }
    }

    removeAllLinks() {
        this.links.splice(0, this.links.length);
    }

    removeAllNodes() {
        this.nodes.splice(0, this.links.length);
    }

    addLink(source, target, value) {
        this.links.push({
            source: this.findNode(source),
            target: this.findNode(target),
            value: value,
        });
    }

    findNode(id) {
        let nodes = this.nodes;
        for (var i in nodes) {
            if (nodes[i].id === id) {
                return nodes[i];
            }
        }
    }

    findNodeIndex(id) {
        let nodes = this.nodes;
        for (var i = 0; i < nodes.length; i++) {
            if (nodes[i].id == id) {
                return i;
            }
        }
    }

    setSize(width, height) {
        this.w = width;
        this.h = height;
    }

    /**
     * This is a hack to make sure the nodes draw on top of the lines
     * @private
     */
    _keepNodesOnTop() {
        $('.' + FORCE_DIRECTED_GRAPH_NODE_STROKE_CLASS).each(function (index) {
            var gnode = this.parentNode;
            gnode.parentNode.appendChild(gnode);
        });
    }

    /**
     * Run the updateFunction. Will internall call update() once the passed
     * updateFunction is complete.
     * @param updateFunction Update function that manipulates this graph object.
     */
    doScopedUpdate(updateFunction) {
        updateFunction();
        this.update();
    }

    update() {
        let vis = this.vis;
        let force = this.force;
        let links = this.links;
        let nodes = this.nodes;
        let w = this.w;
        let h = this.h;

        let link = vis.selectAll('line').data(links, function (d) {
            return d.source.id + '-' + d.target.id;
        });

        link.enter()
            .append('line')
            .attr('id', function (d) {
                return d.source.id + '-' + d.target.id;
            })
            .attr('stroke-width', function (d) {
                return d.value / 10;
            })
            .attr('class', 'force-directed-graph-link link');
        link.append('title').text(function (d) {
            return d.value;
        });
        link.exit().remove();

        var node = vis.selectAll('g.node').data(nodes, function (d) {
            return d.id;
        });

        var nodeEnter = node
            .enter()
            .append('g')
            .attr('class', 'force-directed-graph-node node')
            .call(force.drag);

        nodeEnter
            .append('svg:circle')
            .attr('class', FORCE_DIRECTED_GRAPH_NODE_STROKE_CLASS)
            .attr('r', 5)
            .attr('id', function (d) {
                return 'Node;' + d.id;
            });

        nodeEnter
            .append('svg:text')
            .attr('class', 'force-directed-graph-text')
            .attr('x', 14)
            .attr('y', '.31em')
            .text(function (d) {
                return d.id;
            });

        node.attr('fill', function (d) {
            return d.color;
        });

        node.exit().remove();

        force.on('tick', function () {
            node.attr('transform', function (d) {
                return 'translate(' + d.x + ',' + d.y + ')';
            });

            link.attr('x1', function (d) {
                return d.source.x;
            })
                .attr('y1', function (d) {
                    return d.source.y;
                })
                .attr('x2', function (d) {
                    return d.target.x;
                })
                .attr('y2', function (d) {
                    return d.target.y;
                });
        });

        // Restart the force layout.
        force
            .gravity(0.15)
            .theta(0.8)
            .alpha(0.1)
            .linkStrength(0.1)
            .charge(-120)
            .friction(0.9)
            .size([w, h])
            .start();

        this._keepNodesOnTop();
    }
}

export function createGraph(containerSelector) {
    return new ForceDirectedGraph(containerSelector);
}

export function createNode(id, color?) {
    return new ForceDirectedGraphNode(id, color);
}
